import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/store';

// Define the interface for a comment
export interface IComment {
    commentId: string;
    userId: string;
    userName: string;
    userEmail?: string;
    userProfilePicture?: string;
    contentId: string;
    contentType: 'article' | 'video' | 'audio' | 'book';
    comment: string;
    parentCommentId?: string;
    likes: number;
    likedBy: string[];
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
}

interface GetAllCommentsResponse {
    success: boolean;
    comments: IComment[];
    total: number;
    pages: number;
}

interface DeleteCommentResponse {
    success: boolean;
    message: string;
}

export const commentApi = createApi({
    reducerPath: 'commentApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_API_URL || 'https://api.truthoflife.live',
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as RootState).admin.token;
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Comments'],
    endpoints: (builder) => ({
        getAllComments: builder.query<GetAllCommentsResponse, { page: number; limit: number }>({
            query: ({ page = 1, limit = 20 }) => ({
                url: `/admin/comments?page=${page}&limit=${limit}`,
                method: 'GET',
            }),
            providesTags: ['Comments'],
        }),
        getCommentsByContent: builder.query<{ success: boolean; comments: IComment[]; count: number }, { contentType: string; contentId: string }>({
            query: ({ contentType, contentId }) => ({
                url: `/comments/${contentType}/${contentId}`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [{ type: 'Comments', id: arg.contentId }],
        }),
        deleteComment: builder.mutation<DeleteCommentResponse, string>({
            query: (commentId) => ({
                url: `/admin/comments/${commentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Comments'],
        }),
    }),
});

export const { useGetAllCommentsQuery, useDeleteCommentMutation, useGetCommentsByContentQuery } = commentApi;
